import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

function Extensions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchExtensions();
  }, []);

  const fetchExtensions = async () => {
    try {
      const response = await api.getExtensions();
      setExtensions(response.data);
    } catch (error) {
      console.error('Süre uzatma talepleri yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.approveExtension(id);
      fetchExtensions();
    } catch (error) {
      alert(error.response?.data?.message || 'Onaylama başarısız');
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Red sebebini yazın:');
    if (reason) {
      try {
        await api.rejectExtension(id, reason);
        fetchExtensions();
      } catch (error) {
        alert(error.response?.data?.message || 'Reddetme başarısız');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Bekliyor';
      case 'approved': return 'Onaylandı';
      case 'rejected': return 'Reddedildi';
      default: return status;
    }
  };

  const filteredExtensions = extensions.filter(ext => {
    if (filter === 'all') return true;
    return ext.status === filter;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Süre Uzatma Talepleri</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Tümü ({extensions.length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'pending' ? 'bg-yellow-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Bekleyen ({extensions.filter(e => e.status === 'pending').length})
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'approved' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Onaylı ({extensions.filter(e => e.status === 'approved').length})
          </button>
          <button
            onClick={() => setFilter('rejected')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'rejected' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Reddedilen ({extensions.filter(e => e.status === 'rejected').length})
          </button>
        </div>
      </div>

      {/* Extensions List */}
      <div className="grid gap-4">
        {filteredExtensions.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            Kayıt bulunamadı
          </div>
        ) : (
          filteredExtensions.map((ext) => (
            <div key={ext.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {ext.WorkOrderAssignment?.WorkOrder?.orderNumber || 'İş Emri'}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(ext.status)}`}>
                      {getStatusLabel(ext.status)}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-gray-500">İmalatçı:</span>
                      <p className="font-medium">{ext.WorkOrderAssignment?.Company?.name || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Mevcut Bitiş:</span>
                      <p className="font-medium">{formatDate(ext.originalDueDate)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Talep Edilen:</span>
                      <p className="font-medium text-blue-600">{formatDate(ext.requestedDueDate)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Talep Tarihi:</span>
                      <p className="font-medium">{formatDate(ext.createdAt)}</p>
                    </div>
                  </div>

                  {ext.reason && (
                    <div className="p-3 bg-gray-50 rounded-lg mb-3">
                      <span className="text-gray-500 text-sm">Talep Sebebi:</span>
                      <p className="text-sm">{ext.reason}</p>
                    </div>
                  )}

                  {ext.status === 'approved' && ext.approvedDueDate && (
                    <div className="p-3 bg-green-50 rounded-lg mb-3">
                      <span className="text-gray-500 text-sm">Onaylanan Tarih:</span>
                      <p className="font-medium text-green-700">{formatDate(ext.approvedDueDate)}</p>
                      {ext.ApprovedByUser && (
                        <p className="text-xs text-gray-400 mt-1">
                          Onaylayan: {ext.ApprovedByUser?.name} - {formatDate(ext.approvedAt)}
                        </p>
                      )}
                    </div>
                  )}

                  {ext.status === 'rejected' && ext.rejectionReason && (
                    <div className="p-3 bg-red-50 rounded-lg mb-3">
                      <span className="text-gray-500 text-sm">Red Sebebi:</span>
                      <p className="text-sm text-red-700">{ext.rejectionReason}</p>
                      {ext.RejectedByUser && (
                        <p className="text-xs text-gray-400 mt-1">
                          Reddeden: {ext.RejectedByUser?.name} - {formatDate(ext.rejectedAt)}
                        </p>
                      )}
                    </div>
                  )}

                  <button
                    onClick={() => navigate(`/work-orders/${ext.WorkOrderAssignment?.workOrderId}`)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    İş emrini görüntüle →
                  </button>
                </div>

                {ext.status === 'pending' && (user.userType === 'admin' || user.userType === 'tis') && (
                  <div className="ml-4 flex flex-col space-y-2">
                    <button
                      onClick={() => handleApprove(ext.id)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Onayla
                    </button>
                    <button
                      onClick={() => handleReject(ext.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Reddet
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Extensions;
